import React from 'react';
import { X, ArrowRight } from 'lucide-react';

const EDGE_COLORS = {
  HOLDS: '#f97316',
  EVIDENCES: '#3b82f6',
  RESONATES_WITH: '#ec4899',
  ACTIVATES: '#10b981',
  HAS_RELATIONSHIP: '#f59e0b',
  SUGGESTED_CONNECTION: '#8b5cf6',
  RELATED_TO: '#8b5cf6',
  EXPRESSES_TENSION: '#f59e0b',
  AUTHORED: '#6b7280',
  IN_STORY: '#3b82f6',
  IN_PHOTO: '#3b82f6',
  IN_RECORDING: '#3b82f6',
  IN_DOCUMENT: '#3b82f6',
  APPEARS_IN: '#3b82f6',
};

const SENTIMENT_COLORS = {
  POSITIVE: '#10b981',
  NEGATIVE: '#ef4444',
  NEUTRAL: '#6b7280',
  COMPLEX: '#8b5cf6',
};

export default function EdgeDetailPanel({ link, onClose }) {
  if (!link) return null;

  const color = EDGE_COLORS[link.type] || '#6b7280';
  const sourceLabel = link.source?.label || link.source || '?';
  const targetLabel = link.target?.label || link.target || '?';

  return (
    <div className="w-72 shrink-0 border-l bg-card/50 flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-start justify-between p-3 border-b">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="w-3 h-0.5 shrink-0" style={{ backgroundColor: color }} />
            <p className="text-sm font-medium text-foreground font-mono">{link.type}</p>
          </div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Relationship</p>
        </div>
        <button
          onClick={onClose}
          className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Source → Target */}
      <div className="p-3 border-b">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
          Direction
        </p>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-foreground font-medium truncate max-w-[100px]">{sourceLabel}</span>
          <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
          <span className="text-foreground font-medium truncate max-w-[100px]">{targetLabel}</span>
        </div>
      </div>

      {/* Relationship Type + Sentiment */}
      {(link.relationship_type || link.sentiment) && (
        <div className="px-3 pt-3 pb-1 border-b flex flex-wrap gap-1.5">
          {link.relationship_type && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-accent/50 text-foreground">
              {link.relationship_type}
            </span>
          )}
          {link.other_types?.length > 0 && link.other_types.map((t) => (
            <span key={t} className="px-2 py-0.5 text-[10px] rounded-full bg-accent/30 text-muted-foreground">
              {t}
            </span>
          ))}
          {link.sentiment && (
            <span
              className="px-2 py-0.5 text-[10px] font-medium rounded-full"
              style={{
                backgroundColor: `${SENTIMENT_COLORS[link.sentiment] || '#6b7280'}20`,
                color: SENTIMENT_COLORS[link.sentiment] || '#6b7280',
              }}
            >
              {link.sentiment}
            </span>
          )}
        </div>
      )}

      {/* Weight */}
      {link.weight != null && (
        <div className="px-3 pt-3 pb-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
            Weight
          </p>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(100, link.weight * 100)}%`,
                  backgroundColor: color,
                }}
              />
            </div>
            <span className="text-xs font-mono text-foreground">{link.weight.toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* Reasoning / Quote */}
      {link.reasoning && (
        <div className="px-3 pt-3 pb-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
            Evidence
          </p>
          <p className="text-xs text-foreground leading-relaxed whitespace-pre-wrap">
            {link.reasoning}
          </p>
        </div>
      )}

      {/* Additional Properties */}
      {link.properties && Object.keys(link.properties).length > 0 && (
        <div className="px-3 pt-3 pb-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
            Properties
          </p>
          <div className="space-y-1.5">
            {Object.entries(link.properties).map(([key, value]) => (
              <div key={key} className="text-xs">
                <span className="text-muted-foreground font-mono text-[10px]">{key}</span>
                <p className="text-foreground mt-0.5 break-words">
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No data fallback */}
      {link.weight == null && !link.reasoning && !link.relationship_type && !link.sentiment && (!link.properties || Object.keys(link.properties).length === 0) && (
        <div className="px-3 pt-3 pb-1">
          <p className="text-xs text-muted-foreground">
            No details recorded for this edge.
          </p>
        </div>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Footer — Edge ID */}
      <div className="p-3 border-t">
        <p className="text-[10px] text-muted-foreground font-mono truncate">{link.id}</p>
      </div>
    </div>
  );
}
